import { z } from 'zod/v3';

export const ONBOARDING_ANSWERS_SCHEMA_VERSION =
  'screenata.onboardingAnswers.v1' as const;

const MAX_ONBOARDING_VALUE_BYTES = 32 * 1024;
const MAX_ONBOARDING_VALUE_DEPTH = 5;

function isBoundedJsonValue(value: unknown, depth = 0): boolean {
  if (depth > MAX_ONBOARDING_VALUE_DEPTH) return false;
  if (value == null || typeof value === 'boolean') return true;
  if (typeof value === 'string') return value.length <= MAX_ONBOARDING_VALUE_BYTES;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) {
    return value.length <= 100 && value.every((item) => isBoundedJsonValue(item, depth + 1));
  }
  if (typeof value !== 'object') return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length <= 100 && entries.every(
    ([key, child]) => key.length <= 128 && isBoundedJsonValue(child, depth + 1),
  );
}

const OnboardingAnswerValueSchema = z.unknown().refine(
  (value) => {
    if (!isBoundedJsonValue(value)) return false;
    try {
      return new TextEncoder().encode(JSON.stringify(value)).byteLength <= MAX_ONBOARDING_VALUE_BYTES;
    } catch {
      return false;
    }
  },
  'Answer value exceeds the allowed size or nesting limits.',
);

function rejectDuplicateBy<T>(
  items: T[],
  keyFor: (item: T) => string,
  ctx: z.RefinementCtx,
  label: string,
) {
  const seen = new Set<string>();
  items.forEach((item, index) => {
    const key = keyFor(item);
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate ${label}: ${key}`,
        path: [index],
      });
    }
    seen.add(key);
  });
}

export const OnboardingAnswerSourceSchema = z.enum([
  'repository',
  'public_site',
  'user',
]);

export const OnboardingAnswerReferenceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('file'),
    path: z.string().min(1).max(512),
    locator: z.string().min(1).max(256).optional(),
  }).strict(),
  z.object({
    type: z.literal('url'),
    url: z.string().url().max(2048),
  }).strict(),
  z.object({ type: z.literal('user_confirmation') }).strict(),
]);

export const OnboardingAnswerSchema = z.object({
  questionKey: z.string().min(1).max(128),
  value: OnboardingAnswerValueSchema,
  source: OnboardingAnswerSourceSchema,
  confidence: z.number().min(0).max(1).optional(),
  references: z.array(OnboardingAnswerReferenceSchema).max(20),
}).strict();

export const OnboardingReadinessDraftSchema = z.object({
  blueprintId: z.string().min(1).max(128),
  optionKey: z.string().min(1).max(128),
  rationale: z.string().max(1000).optional(),
  source: z.literal('user'),
}).strict();

export const OnboardingBrandAssetSchema = z.object({
  kind: z.enum(['organization_logo', 'workspace_favicon']),
  sourceUrl: z.string().url().max(2048),
  localPath: z.string().min(1).max(1024),
  contentType: z.enum(['image/png', 'image/jpeg']),
  size: z.number().int().positive().max(2 * 1024 * 1024),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
}).strict();

export const OnboardingAnswersBundleV1Schema = z.object({
  schemaVersion: z.literal(ONBOARDING_ANSWERS_SCHEMA_VERSION),
  generatedAt: z.string().datetime(),
  answers: z.array(OnboardingAnswerSchema).max(100),
  readinessDrafts: z.array(OnboardingReadinessDraftSchema).max(20).optional(),
  readinessDefaultMode: z
    .enum(['apply_to_unanswered', 'leave_unanswered'])
    .optional(),
  brandAssets: z.array(OnboardingBrandAssetSchema).max(2).optional(),
}).strict().superRefine((bundle, ctx) => {
  rejectDuplicateBy(bundle.answers, (answer) => answer.questionKey, ctx, 'answer key');
  rejectDuplicateBy(
    bundle.brandAssets ?? [],
    (asset) => asset.kind,
    ctx,
    'brand asset kind',
  );
});

export const OnboardingApplyAnswerSchema = OnboardingAnswerSchema.omit({
  references: true,
});

export const OnboardingApplyReadinessDraftSchema =
  OnboardingReadinessDraftSchema.omit({ rationale: true });

export const OnboardingApplyRequestSchema = z.object({
  mode: z.enum(['preview', 'apply']),
  approved: z.boolean().default(false),
  answers: z.array(OnboardingApplyAnswerSchema).max(100),
  readinessDrafts: z
    .array(OnboardingApplyReadinessDraftSchema)
    .max(20)
    .optional(),
  readinessDefaultMode: z
    .enum(['apply_to_unanswered', 'leave_unanswered'])
    .default('leave_unanswered'),
}).strict().superRefine((request, ctx) => {
  rejectDuplicateBy(request.answers, (answer) => answer.questionKey, ctx, 'answer key');
});

export const OnboardingBrandUploadRequestSchema = z.object({
  mode: z.enum(['preview', 'apply']),
  approved: z.boolean().default(false),
  kind: z.enum(['organization_logo', 'workspace_favicon']),
  sourceUrl: z.string().url().max(2048),
  filename: z.string().min(1).max(255),
  contentType: z.enum(['image/png', 'image/jpeg']),
  size: z.number().int().positive().max(2 * 1024 * 1024),
  sha256: z.string().regex(/^[a-f0-9]{64}$/i),
  base64Data: z.string().min(1).max(Math.ceil((2 * 1024 * 1024) / 3) * 4 + 4),
}).strict();

export type OnboardingAnswersBundleV1 = z.infer<
  typeof OnboardingAnswersBundleV1Schema
>;
export type OnboardingApplyRequest = z.infer<typeof OnboardingApplyRequestSchema>;
export type OnboardingBrandUploadRequest = z.infer<
  typeof OnboardingBrandUploadRequestSchema
>;

export type OnboardingAnswerPreview = {
  questionKey: string;
  currentValue: unknown;
  proposedValue: unknown;
  sourceClass: 'observed' | 'proposal' | 'user';
  outcome: 'add' | 'replace' | 'unchanged' | 'rejected';
  reason?: string;
};

export type ReadinessDraftPreview = {
  blueprintId: string;
  prompt: string;
  currentOptionKey: string | null;
  proposedOptionKey: string;
  outcome: 'add' | 'replace' | 'unchanged' | 'rejected';
  reason?: string;
};

export type OnboardingApplyResponse = {
  mode: 'preview' | 'apply';
  answers: OnboardingAnswerPreview[];
  readinessDrafts: ReadinessDraftPreview[];
  readinessDefaults: {
    mode: 'apply_to_unanswered' | 'leave_unanswered';
    eligible: number;
    applied: number;
  };
  savedFields: string[];
  rolesSaved: number;
  browserReviewUrl: string;
};

export type OnboardingBrandPreview = {
  kind: 'organization_logo' | 'workspace_favicon';
  currentUrl: string | null;
  proposedThumbnailDataUrl: string;
  sourceUrl: string;
  managedUrl: string | null;
  contentType: 'image/png' | 'image/jpeg';
  size: number;
  width: number;
  height: number;
  sha256: string;
  outcome: 'add' | 'replace' | 'unchanged';
};

export type AgentOnboardingOperationalPrompt = {
  id: string;
  onboardingQuestion: {
    questionKey: string;
    prompt: string;
    options: string[];
    recommendedIndex: number | null;
  } | null;
  readinessQuestions: Array<{
    blueprintId: string;
    prompt: string;
    options: Array<{
      key: string;
      label: string;
      recommended: boolean;
    }>;
  }>;
};
